import React, { useState, useEffect } from "react";
import Header from "../../../components/Header/Header";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AddIndent = () => {
  const [formData, setFormData] = useState({
    ProjectID: "",
    TypeID: "",
    ProjectName: "",
    L1A: false,
    L2: false,
    TaskSchedule: "",
  });
  const [projectOptions, setProjectOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [filteredStages, setFilteredStages] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const role = useSelector((state) => state.auth.role);
  const enteredBy = useSelector((state) => state.auth.id);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const projectResponse =
          role === "admin"
            ? await axios.get(`${API_BASE_URL}/dropdown/mprojects`)
            : await axios.get(`${API_BASE_URL}/dropdown/mprojects2`, {
                params: { ProjectIncharge: enteredBy },
              });

        const [stages, types] = await Promise.all([
          axios.get(`${API_BASE_URL}/dropdown/mstages`),
          axios.get(`${API_BASE_URL}/dropdown/mindenttypes`)
        ]);

        setProjectOptions(projectResponse.data);
        setStageOptions(stages.data);
        setTypeOptions(types.data);

        const availableType = types.data[0];
        if (availableType) {
          setFormData(prev => ({
            ...prev,
            TypeID: availableType.TypeID.toString()
          }));
        }
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
        setError("Failed to load dropdown data.");
      } finally {
        setLoading(false);
      }
    };

    if (enteredBy) fetchData();
  }, [enteredBy, role]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "ProjectID") {
      const projectStages = stageOptions.filter((stage) =>
        projectOptions.some(
          (project) =>
            project.ProjectID === value && project.StageID === stage.StageID
        )
      );
      setFilteredStages(projectStages);
      const defaultStage =
        projectStages.length > 0 ? projectStages[0].StageID : "";
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        StageID: defaultStage,
      }));
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const selectedProject = projectOptions.find(p => p.ProjectID === parseInt(formData.ProjectID));
    const selectedType = typeOptions.find(t => t.TypeID.toString() === formData.TypeID);
  
    const dataToSend = {
      UserID: enteredBy,
      ProjectID: formData.ProjectID,
      ProjectName: selectedProject?.ProjectName || '',
      TypeID: formData.TypeID,
      CreationDate: new Date().toISOString().split("T")[0],
      Status: "Active",
      L1A: formData.L1A,
      L2: formData.L2,
      TaskSchedule: formData.TaskSchedule,
    };
  
    try {
      const response = await axios.post(`${API_BASE_URL}/indent/indent`, dataToSend);
  
      const newIndentRow = {
        IndentID: response.data.indentId,
        ProjectName: selectedProject?.ProjectName,
        TypeName: selectedType?.TypeName,
        CreationDate: dataToSend.CreationDate,
        Status: dataToSend.Status,
        L1: formData.L1 ? "Approved" : "Pending",
        L2: formData.L2 ? "Approved" : "Pending",
        PO: "Pending",
        TaskSchedule: dataToSend.TaskSchedule,
        ItemCount: 0,
      };
  
      navigate("/PETable", { state: { row: newIndentRow } });
    } catch (err) {
      console.error("Error adding indent:", err);
      setError(err.response?.data?.error || "Failed to add indent.");
    }
  };

  const handleCancel = () => {
    navigate("/peindent");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-md mx-auto">
        <h1 className="text-2xl sm:text-3xl font-semibold text-center text-gray-900 mb-8">
          Add Indent
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
          <div>
            <label
              htmlFor="ProjectID"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Project
            </label>
            <select
              id="ProjectID"
              name="ProjectID"
              value={formData.ProjectID}
              onChange={handleInputChange}
              required
              className="block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            >
              <option value="">Select project</option>
              {projectOptions.map((option) => (
                <option key={option.ProjectID} value={option.ProjectID}>
                  {option.ProjectName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="TypeID"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Type
            </label>
            <select
              id="TypeID"
              name="TypeID"
              value={formData.TypeID}
              onChange={handleInputChange}
              required
              className="block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            >
              <option value="">Select type</option>
              {typeOptions.map((option) => (
                <option key={option.TypeID} value={option.TypeID}>
                  {option.TypeName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="TaskSchedule"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Task Schedule
            </label>
            <input
              type="text"
              id="TaskSchedule"
              name="TaskSchedule"
              value={formData.TaskSchedule}
              onChange={handleInputChange}
              className="block w-full px-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
              placeholder="Enter task schedule"
            />
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full px-4 py-3 text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full px-4 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddIndent;